import React, { useState, useEffect, useCallback, useRef } from 'react';

import { useRouter } from 'next/navigation';

import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog,
  DialogContent,
  IconButton,
  useMediaQuery,
  useTheme,
  Slide,
  Box,
  Typography,
  Button,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';

import { IdentityRow, identityFromUserSearchResult } from '@/components/identity';
import { traceAction } from '@/feedback/actionBreadcrumb';
import { FEEDBACK_ACTION } from '@/feedback/ontology/types';
import { useUserSearch } from '@/hooks/useUserSearch';
import { DOC_VISIBILITY, DocVisibility, NewDocRequest, asPublicDocId } from '@/lib/api';
import { DisplayConceptRef } from '@/lib/api';
import { API_URL } from '@/lib/consts';
import { getSubmitShortcutLabel } from '@/lib/utils';
import { createElevationStyles } from '@/theme/elevationStyles';

import { ComposeBox, ComposeBoxHandle } from './ComposeBox';
import { VisibilitySelector } from './Visibility';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/** Sample topics shown when the omni-search dialog needs starter prompts. */
export const EXAMPLE_TOPICS: DisplayConceptRef[] = [
  {
    cref: '@en:juicer',
    display: 'juicer',
    description:
      'A toaster is a small kitchen appliance designed to toast multiple types of bread products.',
    is_product: false,
  },
  {
    cref: '@en:gaming tv',
    display: 'gaming tv',
    description: 'An armchair is a comfortable chair with side supports for the arms.',
    is_product: false,
  },
  {
    cref: '@en:ski gear',
    display: 'ski gear',
    description: 'A television (TV) is a device used to display moving images and sound.',
    is_product: false,
  },
];

/** Sample questions shown when the omni-search dialog needs starter prompts. */
export const EXAMPLE_QUESTIONS: string[] = [
  "what's the best toaster under $50?",
  'how do I choose a comfortable office chair?',
  "i'm looking for something to play music from streaming services, but with a built in amp",
];

interface OmniSearchBoxProps {
  onClose: () => void;
  example_topics?: DisplayConceptRef[];
  example_questions?: string[];
}

/** Provides the modal omni-search entry point for questions, topics, and posts. */
export const OmniSearchBox: React.FC<OmniSearchBoxProps> = (props) => {
  const router = useRouter();
  const theme = useTheme();
  const fullScreen = useMediaQuery('(max-width:640px)');
  const elevationStyles = createElevationStyles(theme, 4);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<DisplayConceptRef[]>([]);
  const { results: userResults } = useUserSearch({ query: searchText });
  const composeBoxRef = useRef<ComposeBoxHandle>(null);
  const [searchStatus, setSearchStatus] = useState('');
  const [visibility, setVisibility] = useState<DocVisibility>(DOC_VISIBILITY.Public);
  const [showVisibilityDropdown, setShowVisibilityDropdown] = useState(false);
  const [submitShortcutLabel, setSubmitShortcutLabel] = useState('Ctrl+Enter or Cmd+Enter');

  useEffect(() => {
    setSubmitShortcutLabel(getSubmitShortcutLabel());
  }, []);

  useEffect(() => {
    // Focus the compose box when the component mounts
    const timer = setTimeout(() => {
      composeBoxRef.current?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // TODO(1): Fix text input focus on mobile - keyboard doesn't open automatically
  // Possible solutions:
  // 1. Make collapsed & expanded OmniBox the same element that transitions/expands
  //    so the initial tap naturally focuses the input
  // 2. Use Dialog's onTransitionEnd to focus after animation completes
  // 3. Consider making compose a separate route on mobile instead of overlay

  const searchConcepts = useCallback(async (query: string) => {
    if (!query) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/schema/prefix_search/${encodeURIComponent(query)}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Search failed');
      }
      const data: DisplayConceptRef[] = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Error searching concepts:', error);
      setSearchResults([]);
    }
  }, []);

  const handlePost = useCallback(async () => {
    if (!searchText.trim()) return;
    try {
      // Feedback breadcrumb: record the question-post attempt and its success/failure outcome.
      const data = await traceAction(FEEDBACK_ACTION.POST_QUESTION, async () => {
        const requestBody: NewDocRequest = {
          content: searchText,
          topic: '',
          visibility,
        };
        const response = await fetch(`${API_URL}/doc/new`, {
          credentials: 'include',
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        });
        if (!response.ok) {
          throw new Error('Failed to post content');
        }
        return response.json();
      });
      props.onClose();
      router.push(`/doc/${asPublicDocId(data.doc_id)}?processing_started=1`);
    } catch (err) {
      console.error('Error posting content:', err);
    }
  }, [searchText, visibility, props, router]);

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchConcepts(searchText);
    }, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchText, searchConcepts]);

  // Update search status when results change (for screen readers)
  useEffect(() => {
    if (searchText) {
      const totalResults = searchResults.length + userResults.length;
      setSearchStatus(totalResults > 0 ? `Found ${totalResults} results` : 'No results found');
    } else {
      setSearchStatus('');
    }
  }, [searchResults, userResults, searchText]);

  return (
    <Dialog
      open={true}
      onClose={props.onClose}
      fullScreen={fullScreen}
      TransitionComponent={Transition}
      fullWidth
      aria-labelledby="omnisearch-dialog-title"
      aria-describedby="omnisearch-dialog-description"
      sx={{
        '& .MuiDialog-paper': {
          margin: 0,
          ...(fullScreen && {
            marginTop: 0,
            height: '100%',
            // Account for keyboard on mobile by using vh units
            maxHeight: 'calc(100vh - env(safe-area-inset-bottom))',
            borderRadius: 0,
          }),
          ...(!fullScreen && {
            margin: '32px',
            maxWidth: theme.breakpoints.values.md,
            height: '80vh',
            borderRadius: 3, // Consistent rounded corners on desktop
            ...elevationStyles,
            boxShadow: theme.shadows[8],
            border: `1px solid ${theme.palette.divider}`,
          }),
        },
        '& .omnisearch-result:not(.post-question):hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
        '& .omnisearch-result:not(.post-question):focus': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          outline: 'none',
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          height: '100%',
          ...(fullScreen && {
            // Adjust content area for mobile keyboard
            pb: 'env(keyboard-inset-height, 0)',
          }),
        }}
      >
        {/* Hidden title and description for screen readers */}
        <Box sx={{ position: 'absolute', left: '-9999px' }}>
          <Typography id="omnisearch-dialog-title" variant="h1">
            Search and ask questions
          </Typography>
          <Typography id="omnisearch-dialog-description" variant="body2">
            Type to search for topics and people. Press {submitShortcutLabel} to post, or use the
            Post button below.
          </Typography>
          {/* Live region for search status announcements */}
          <div role="status" aria-live="polite" aria-atomic="true">
            {searchStatus}
          </div>
        </Box>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            props.onClose();
          }}
          aria-label="Close search"
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 10,
            backgroundColor: 'rgba(128, 128, 128, 0.85)',
            '&:hover': {
              backgroundColor: 'rgba(128, 128, 128, 1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          sx={{
            p: 2,
            pb: 1,
            flexShrink: 0,
            position: 'relative',
            zIndex: 1,
            // Override the Card styling in ComposeBox to make it transparent inline
            '& .MuiCard-root': {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              '& .MuiCardContent-root': { padding: 0 },
            },
          }}
        >
          <ComposeBox
            ref={composeBoxRef}
            onClose={props.onClose}
            topic=""
            placeholder="Use @ to tag or invite friends or give platform @feedback."
            content={searchText}
            onContentChange={setSearchText}
            onSubmit={handlePost}
            show_hints={false}
            show_controls={false}
            minRows={fullScreen ? 2 : 3}
          />
          {searchText.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Press {submitShortcutLabel} to post, or use the Post button below. Enter inserts a new
              line.
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 2 }}>
          <Box sx={{ mt: 0 }} role="listbox" aria-label="Search results">
            {!searchText ? (
              <Box sx={{ textAlign: 'left', p: 0 }}>
                <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                  Search products
                </Typography>
                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mb: 2 }}>
                  {props.example_topics?.map((topic, index) => (
                    <Box
                      component="li"
                      key={index}
                      role="option"
                      aria-selected="false"
                      onClick={() => setSearchText(topic.display)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSearchText(topic.display);
                        }
                      }}
                      tabIndex={0}
                      sx={{
                        p: 1,
                        mb: 0.5,
                        borderRadius: 1,
                        cursor: 'pointer',
                        backgroundColor: 'action.hover',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: 'action.selected',
                          transform: 'translateX(4px)',
                        },
                      }}
                    >
                      &ldquo;{topic.display}&rdquo;
                    </Box>
                  ))}
                </Box>
                <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                  Or ask a question
                </Typography>
                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mb: 2 }}>
                  {props.example_questions?.map((question, index) => (
                    <Box
                      component="li"
                      key={index}
                      role="option"
                      aria-selected="false"
                      onClick={() => setSearchText(question)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSearchText(question);
                        }
                      }}
                      tabIndex={0}
                      sx={{
                        p: 1,
                        mb: 0.5,
                        borderRadius: 1,
                        cursor: 'pointer',
                        backgroundColor: 'action.hover',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: 'action.selected',
                          transform: 'translateX(4px)',
                        },
                      }}
                    >
                      &ldquo;{question}&rdquo;
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              <>
                {/* Topics Section */}
                {searchResults.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                      Topics
                    </Typography>
                    {searchResults.map((result, index) => (
                      <Box
                        key={index}
                        role="option"
                        aria-selected="false"
                        onClick={() => {
                          props.onClose();
                          router.push(`/guide/${encodeURIComponent(result.cref)}`);
                        }}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            props.onClose();
                            router.push(`/guide/${encodeURIComponent(result.cref)}`);
                          }
                        }}
                        sx={{
                          p: 1.5,
                          mb: 0.5,
                          borderRadius: 1,
                          cursor: 'pointer',
                          backgroundColor: 'action.hover',
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: 'action.selected',
                            transform: 'translateX(4px)',
                          },
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{ fontWeight: 500, color: 'primary.main' }}
                        >
                          {result.display}:
                        </Typography>{' '}
                        <Typography component="span" variant="body2" color="text.secondary">
                          {result.description}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Users Section */}
                {userResults.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                      Users
                    </Typography>
                    {userResults.map((user) => {
                      const identity = identityFromUserSearchResult(user);

                      return (
                        <Box
                          key={user.username}
                          role="option"
                          aria-selected="false"
                          onClick={() => {
                            props.onClose();
                            router.push(`/citizen/${encodeURIComponent(user.username)}`);
                          }}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              props.onClose();
                              router.push(`/citizen/${encodeURIComponent(user.username)}`);
                            }
                          }}
                          sx={{
                            p: 1.5,
                            mb: 0.5,
                            borderRadius: 1,
                            cursor: 'pointer',
                            backgroundColor: 'action.hover',
                            transition: 'background-color 0.2s',
                            '&:hover': {
                              backgroundColor: 'action.selected',
                            },
                          }}
                        >
                          <IdentityRow
                            identity={identity}
                            linkMode="text"
                            avatarSize={32}
                            showFullname={true}
                          />
                          {user.shared_topics.length > 0 ? (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                              Shared topics: {user.shared_topics.map((t) => t.display).join(', ')}
                            </Typography>
                          ) : null}
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* Post this question card */}
                {searchText && searchResults.length + userResults.length < 10 && (
                  <Box
                    className="omnisearch-result post-question"
                    sx={{
                      p: 1.5,
                      mb: 0.5,
                      borderRadius: 1,
                      backgroundColor: 'action.hover',
                      transition: 'all 0.2s',
                    }}
                  >
                    <Typography>Post this question to AI, friends, or the community</Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 2,
                        mt: 1,
                      }}
                    >
                      <VisibilitySelector
                        visibility={visibility}
                        setVisibility={setVisibility}
                        showDropdown={showVisibilityDropdown}
                        setShowDropdown={setShowVisibilityDropdown}
                        position="below"
                      />
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handlePost}
                        title={`Post (${submitShortcutLabel})`}
                      >
                        Post
                      </Button>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
